<template>
  <section>
    <coozzy-card class="global-card no-top-border table-card">
      <div class="row align-items-center my-2">
        <div class="col-12 col-md-2 mb-2 mb-md-0">
          <coozzy-form-input
            ref="searchText"
            v-model="searchText"
            :placeholder="$t('message.generic.search')"
            @keyup="searchInputText()" />
        </div>
        <div class="col-12 col-md-2 mb-2 mb-md-0">
          <coozzy-multiselect
            v-if="categoryFilter.length > 0"
            v-model="searchCategories"
            :clear-on-select="false"
            :close-on-select="false"
            :custom-label="customLabelCategory"
            :multiple="true"
            :options="categoryFilter"
            :placeholder="$tc('message.onBoarding.devices.filter.category')"
            :preselect-first="false"
            :preserve-search="true"
            :taggable="false"
            :track-by="'value'"
            class="filter-inputs"
            @remove="filterPlansList"
            @select="filterPlansList" />
        </div>
      </div>
      <div
        class="row mb-2">
        <div class="col-12 col-md-2 mt-2 mb-md-0">
          <label
            for="propertyValue"
            class="font-weight-normal">{{ $t('message.onBoarding.investmentPlanning.planningCosts.totalPlanningCosts') }}</label>
          <coozzy-form-input-numeric
            id="propertyValue"
            ref="propertyValue"
            v-model="localPropertyValue"
            type="number"
            class="mb-2"
            :disabled="true"
            :filter="'formatPrice'"
            :placeholder="$tc('message.onBoarding.investmentPlanning.propertyValue')"
            :is-decimal="true" />
        </div>
        <template v-for="item in planningCostsByYear">
          <div
            :key="item.year"
            class="col-12 col-md-2 mt-2 mb-md-0">
            <label
              for="planningCostsByYear"
              class="font-weight-normal">{{ $t('message.onBoarding.investmentPlanning.planningCosts.planningCostsByYear', { year: item.year }) }}</label>
            <coozzy-form-input-numeric
              id="planningCostsByYear"
              ref="planningCostsByYear"
              v-model="item.value"
              type="number"
              class="mb-2"
              :disabled="true"
              :filter="'formatPrice'"
              :is-decimal="true" />
          </div>
        </template>
      </div>
      <div class="row align-items-center mb-2">
        <div
          v-if="hasInvestmentPlanRole"
          class="col-md-2 col-sm-6 col-12">
          <coozzy-dropdown
            :text="selectedEntriesText"
            class="w-100"
            design="green"
            size="sm">
            <coozzy-dropdown-item
              :disabled="selectedEntriesGlobal.length === 0"
              @click="bulkEditPlans('plannedDate')">
              {{ $t('message.onBoarding.investmentPlanning.actions.enterPlannedDate') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              :disabled="selectedEntriesGlobal.length === 0"
              @click="bulkEditPlans('plannedCosts')">
              {{ $t('message.onBoarding.investmentPlanning.actions.enterPlannedCosts') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              :disabled="selectedEntriesGlobal.length === 0"
              @click="deletePlansClicked">
              {{ $t('message.onBoarding.investmentPlanning.actions.deletePlannedCosts') }}
            </coozzy-dropdown-item>
          </coozzy-dropdown>
        </div>
        <div
          v-if="hasInvestmentPlanRole"
          class="col-md-2 col-sm-6 col-12">
          <template v-if="selectAllVisibleEntries && nbrTotalOfHits > nbrPerPage && nbrTotalOfHits <= 1000">
            <coozzy-form-checkbox
              v-model="selectAllEntries"
              :initial="selectAllEntries"
              :class="'p-0 w-100 btn-height-auto'"
              name="selectAllEntries"
              button>
              {{
                selectAllEntries ? $t('message.generic.unselectHiddenEntries', {amount: nbrTotalOfHits}) : $t('message.generic.selectHiddenEntries', {amount: nbrTotalOfHits})
              }}
            </coozzy-form-checkbox>
          </template>
        </div>
        <div
          v-if="hasInvestmentPlanRole"
          class="offset-md-5 col-md-1 d-flex justify-content-end align-items-center">
          <div
            @click="openAssembleListModal()">
            <coozzy-filters-icon
              v-b-tooltip="{container: '#rowConfig', title: $t('message.generic.tooltipAssembleList')}" />
          </div>
        </div>
        <div
          class="col-md-2 col-sm-6 col-12 mt-2 mt-sm-0"
          :class="hasInvestmentPlanRole ? '' : 'offset-sm-6 offset-md-10'">
          <coozzy-form-select
            v-model="nbrPerPage"
            name="numberEntries">
            <option value="5">
              5 {{ $t('message.generic.entries') }}
            </option>
            <option value="10">
              10 {{ $t('message.generic.entries') }}
            </option>
            <option value="15">
              15 {{ $t('message.generic.entries') }}
            </option>
            <option value="20">
              20 {{ $t('message.generic.entries') }}
            </option>
            <option value="all">
              {{ $t('message.manageInterestedParties.filter.allEntries') }}
            </option>
          </coozzy-form-select>
        </div>
      </div>
      <div
        v-if="isBusy"
        class="text-center">
        <coozzy-spinner />
      </div>
      <b-table
        v-else
        id="plansTable"
        ref="plansTable"
        bordered
        hover
        responsive="true"
        stacked="md"
        select-mode="single"
        class="overflow-auto shadow"
        :fields="fields"
        :items="plansList"
        :tbody-tr-class="rowClass">
        <template
          #head(checkbox)>
          <coozzy-form-checkbox
            v-if="hasInvestmentPlanRole"
            ref="header_checkbox"
            @change="(value) => { handleCheckboxesInsideTable(value) }" />
        </template>
        <template #head(description)>
          {{ $t('message.tableColumnHeadings.description') }}
        </template>
        <template #head(bkp)>
          {{ $t('message.tableColumnHeadings.bkp') }}
        </template>
        <template #head(plannedCostDescription)>
          {{ $t('message.tableColumnHeadings.plannedCostDescription') }}
        </template>
        <template #head(plannedDate)>
          {{ $t('message.tableColumnHeadings.plannedDate') }}
        </template>
        <template #head(plannedCosts)>
          {{ $t('message.tableColumnHeadings.plannedCosts') }}
        </template>
        <!-- Body -->
        <template
          #cell(checkbox)="data">
          <coozzy-form-checkbox
            v-if="((source === 'property' && data.item.referenceId.startsWith('prop')) || source === 'building') && hasInvestmentPlanRole"
            :id="'check_' + data.item.id"
            :key="'checkbox_' + data.item.id"
            @change="(value) => { itemSelectedGlobal(data.item, value) }" />
        </template>
        <template #cell(description)="data">
          <template v-if="data.item.referenceId.includes('prop_')">
            {{ $t('message.propertiesView.property') }} {{ getPropertyName() }}
          </template>
          <template v-if="data.item.referenceId.includes('building_')">
            {{ $t('message.onBoardingTabs.building') }}
            {{ data.item.referenceId ? getBuildingName(data.item.referenceId) : '' }}
          </template>
          <template v-if="data.item.referenceId.includes('obj_')">
            {{ $t('message.onBoarding.buildings.objects.titleObject') }}
            {{ data.item.referenceId ? getObjectName(data.item.referenceId) : '' }}
          </template>
        </template>
        <template #cell(bkp)="data">
          <coozzy-form-input
            :id="'bkpH_' + data.item.id"
            v-model="data.item.bkp"
            :is-read-only="readOnly"
            :allow-click-input-on-table="true"
            :disabled="(source === 'property' && !data.item.referenceId.startsWith('prop')) || !hasInvestmentPlanRole"
            @input="emitChanges(data.item)" />
        </template>
        <template #cell(plannedCostDescription)="data">
          <coozzy-form-input
            :id="'plannedCostDescription_' + data.item.id"
            v-model="data.item.name"
            :is-read-only="readOnly"
            :allow-click-input-on-table="true"
            :disabled="(source === 'property' && !data.item.referenceId.startsWith('prop')) || !hasInvestmentPlanRole"
            @input="emitChanges(data.item)" />
        </template>
        <template #cell(plannedDate)="data">
          <coozzy-form-input
            :id="'plannedDate_' + data.item.id"
            v-model="data.item.dateString"
            :is-read-only="readOnly"
            :filter="'formatDate'"
            type="date"
            :allow-click-input-on-table="true"
            :disabled="(source === 'property' && !data.item.referenceId.startsWith('prop')) || !hasInvestmentPlanRole"
            @blur="dateToObject(data.item)" />
        </template>
        <template #cell(plannedCosts)="data">
          <coozzy-form-input-numeric
            :id="'plannedCosts_' + data.item.id"
            v-model="data.item.costs"
            type="number"
            :is-read-only="readOnly"
            :filter="'formatPrice'"
            placeholder="CHF"
            :is-decimal="true"
            :allow-click-input-on-table="true"
            :disabled="(source === 'property' && !data.item.referenceId.startsWith('prop')) || !hasInvestmentPlanRole"
            @input="calculatePlanningCostsByYear(data.item)" />
        </template>
      </b-table>
      <b-pagination
        v-if="nbrPerPage !== 'all'"
        v-model="currentPage"
        :per-page="nbrPerPage"
        :total-rows="listPlans.length"
        align="center"
        class="mb-3" />
    </coozzy-card>
    <b-modal
      v-if="showCreatePlanModal"
      :id="'createPlanningCostModal'"
      ref="createPlanningCostModal"
      :title="$t('message.onBoarding.investmentPlanning.addPlanningCosts')"
      hide-footer
      no-close-on-backdrop>
      <template>
        <div class="col p-0">
          <b-table
            id="table-groups"
            ref="table-groups"
            :busy="loadingAddPlan"
            :fields="fieldsAddPlan"
            :items="listPropBuildObj"
            bordered
            class="mb-4"
            hover
            responsive="true"
            stacked="md">
            <!-- Busy state -->
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <coozzy-spinner />
              </div>
            </template>
            <template #head(category)>
              {{ $t('message.advertisement.objectCategory.name') }}
            </template>
            <template #head(name)>
              {{ $t('message.onBoarding.devices.description') }}
            </template>
            <!-- Body -->
            <template #cell(checkbox)="data">
              <coozzy-form-checkbox
                :id="data.item.id"
                @change="(value) => { itemSelected(data.item, value) }" />
            </template>
            <template #cell(category)="data">
              <template v-if="data.item.id.startsWith('building_')">
                {{ $t('message.generic.building') }}
              </template>
              <template v-else-if="data.item.id.startsWith('prop_')">
                {{ $t('message.propertiesView.tabs.property') }}
              </template>
              <template v-else-if="data.item.category && data.item.category !== 'UNDEFINED_CATEGORY'">
                {{ $t('message.onBoarding.buildings.objects.category.' + data.item.category.toLowerCase()) }}
              </template>
              <template v-else>
                -
              </template>
            </template>
            <template #cell(name)="data">
              {{ data.item.name }}
            </template>
          </b-table>
        </div>
        <div class="col p-0">
          <coozzy-button
            class="mb-0 border"
            design="transparent"
            size="small"
            @click="hideCreatePlanModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            :disabled="selectedEntries.length < 1"
            class="float-right mb-0"
            design="green"
            size="small"
            @click="createPlanClicked()">
            {{ $t('message.generic.add') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-if="showDeleteModal"
      :id="'confirmationModalDeletePlans'"
      ref="confirmationModalDeletePlans"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.onBoarding.investmentPlanning.deleteModal.title')"
      @close="hideDeletePlanModal">
      <template>
        <div class="col p-0">
          <p>{{ $t('message.onBoarding.investmentPlanning.deleteModal.textPlanningCosts') }}</p>
        </div>
        <div class="col p-0">
          <coozzy-button
            size="small"
            class="mb-0 border"
            design="transparent"
            @click="hideDeletePlanModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            class="float-right mb-0"
            design="green"
            @click="deletePlan()">
            {{ $t('message.generic.delete') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-if="showEditModal"
      :id="'editPlansModal'"
      ref="editPlansModal"
      no-close-on-backdrop
      hide-footer
      :title="titleEditModal"
      @close="hideEditPlansModal">
      <template>
        <div
          v-if="editAction === 'plannedDate'"
          class="col p-0">
          <p>{{ $t('message.onBoarding.investmentPlanning.subTitlesEditModal.enterPlannedDate') }}</p>
          <coozzy-form-input
            v-model="newPlannedDate"
            type="date" />
        </div>
        <div
          v-if="editAction === 'plannedCosts'"
          class="col p-0">
          <p>{{ $t('message.onBoarding.investmentPlanning.subTitlesEditModal.enterPlannedCosts') }}</p>
          <coozzy-form-input
            v-model="newPlannedCost"
            type="number" />
        </div>
        <div class="col p-0 mt-3">
          <coozzy-button
            size="small"
            class="mb-0 border"
            design="transparent"
            @click="hideEditPlansModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            class="float-right mb-0"
            design="green"
            @click="applyEditChanges()">
            {{ $t('message.generic.form.applyAdd') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <assemble-list-modal
      ref="assembleListModal"
      modal-id="assembleListModal"
      :fields="selectedFields"
      :default-fields="dataFields"
      @save-fields="saveFields" />
  </section>
</template>

<script lang="ts">
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { routeChecks } from '@/mixins/routeChecks'
import { tables } from '@/mixins/tables'
import { user } from '@/mixins/user'
import { buildValueCounts } from '@/misc/utils/filterCounts'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import debounce from 'debounce'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyDropdownItem from '@/framework/components/dropdown/CoozzyDropdownItem.vue'
import { componentUtils } from '@/mixins/componentUtils'
import { dateUtils } from '@/mixins/dateUtils'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import { helper } from '@/types/helper'
import Vue from 'vue'
import CoozzyDropdown from '@/framework/components/dropdown/CoozzyDropdown.vue'
import CoozzyFormInputNumeric from '@/framework/components/form/input/CoozzyFormInputNumeric.vue'
import CoozzyFiltersIcon from '@/framework/components/icons/CoozzyFiltersIcon.vue'
import SearchApi from '@/misc/apis/SearchApi'
import AssembleListModal from '@/contact/components/AssembleListModal.vue'
import { InvestPlanPlannedCosts } from '@/types/api'
import CoozzyMultiselect from '@/framework/components/multiselect/CoozzyMultiselect.vue'

interface SelectablePlan extends InvestPlanPlannedCosts {
  selected: boolean;
}

interface queryParams {
  view: string
  searchText?: string
  searchCategories?: string[]
}

export default Vue.extend({
  name: 'PlanningCosts',
  components: {
    CoozzyMultiselect,
    AssembleListModal,
    CoozzyFiltersIcon,
    CoozzyFormInputNumeric,
    CoozzyDropdown,
    CoozzyButton,
    CoozzyDropdownItem,
    CoozzySpinner,
    CoozzyFormInput,
    CoozzyCard,
    CoozzyFormSelect,
    CoozzyFormCheckbox
  },
  mixins: [routeChecks, tables, user, componentUtils, dateUtils, helper],
  props: {
    property: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    addNewComponent: {
      type: Boolean,
      default: false
    },
    listPlanningCosts: {
      type: Array,
      default: () => []
    },
    buildingsList: {
      default: () => [],
      type: Array
    },
    objectsList: {
      default: () => [],
      type: Array
    },
    source: {
      type: String,
      default: 'property' // 'property' or 'building'
    },
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isBusy: true,
      selectedEntries: [],
      nbrPerPage: '5' as string,
      currentPage: 1,
      fields: [
        {
          key: 'checkbox',
          label: '',
          thClass: 'th-checkbox',
          tdClass: 'align-middle border-right-0 checkbox-cell'
        }
      ],
      dataFields: [
        {
          key: 'description',
          label: this.$t('message.tableColumnHeadings.description'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'bkp',
          label: this.$t('message.tableColumnHeadings.bkp'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'plannedCostDescription',
          label: this.$t('message.tableColumnHeadings.plannedCostDescription'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'plannedDate',
          label: this.$t('message.tableColumnHeadings.plannedDate'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'plannedCosts',
          label: this.$t('message.tableColumnHeadings.plannedCosts'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        }
      ],
      selectedFields: [],
      selectedEntriesGlobal: [],
      selectAllVisibleEntries: false,
      selectAllEntries: false,
      searchText: '',
      searchCategories: [],
      debouncedFilterPlansList: null,
      closed: false,
      typeSuggestInputProps: {
        id: 'autosuggest__input',
        placeholder: this.$t('message.onBoarding.devices.searchType'),
        class: 'custom-select allowClickInputOnTable form-control'
      },
      fieldsAddPlan: [
        {
          key: 'checkbox',
          label: '',
          thClass: 'th-checkbox',
          tdClass: 'align-middle border-right-0 checkbox-cell'
        },
        {
          key: 'category',
          label: this.$t('message.advertisement.objectCategory.name'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'name',
          label: this.$t('message.onBoarding.devices.description'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        }
      ],
      loadingAddPlan: false,
      showCreatePlanModal: false,
      allPlans: [],
      showDeleteModal: false,
      showEditModal: false,
      editAction: '',
      newPlannedDate: '',
      newPlannedCost: 0,
      listColoredItems: [],
      currentYear: new Date().getFullYear(),
      listPlans: [],
      planningCostsByYear: [
        { year: new Date().getFullYear(), value: 0 },
        { year: new Date().getFullYear() + 1, value: 0 },
        { year: new Date().getFullYear() + 2, value: 0 },
        { year: new Date().getFullYear() + 3, value: 0 }
      ],
      modalHideHandler: null
    }
  },
  computed: {
    titleEditModal() {
      switch (this.editAction) {
        case 'plannedDate':
          return this.$t('message.onBoarding.investmentPlanning.titlesEditModal.titlePlannedDate')
        case 'plannedCosts':
          return this.$t('message.onBoarding.investmentPlanning.titlesEditModal.titlePlannedCosts')
        default:
          return ''
      }
    },
    localPropertyValue: {
      get() {
        return this.listPlans.reduce((sum, plan) => {
          if (!plan.date || plan.date?.year >= this.currentYear) {
            return sum + (parseFloat(plan.costs) || 0)
          }
          return sum
        }, 0)
      },
      set(value) {
        this.$emit('property-value-changed', value)
      }
    },
    listPropBuildObj() {
      if (this.source === 'property') {
        return this.property ? [this.property] : []
      }
      return this.buildingsList.concat(this.objectsList)
    },
    selectedEntriesText() {
      const count = this.selectAllEntries && this.selectAllVisibleEntries ? this.nbrTotalOfHits : this.selectedEntriesGlobal.length
      return this.$t('message.generic.selectedEntries') + ':  ' + count
    },
    plansList: {
      get() {
        let list = []
        if (this.listPlans && this.listPlans.length > 0) {
          list = this.listPlans
        }
        if (this.nbrPerPage !== 'all') {
          list = list.slice((this.currentPage - 1) * this.nbrPerPage, this.currentPage * this.nbrPerPage)
        }
        return list
      },
      set(value) {
        this.$emit('change-plan', value)
      }
    },
    isFiltered() {
      return this.searchText !== '' || this.searchCategories.length > 0
    },
    categoryFilter() {
      if (this.allPlans.length === 0) {
        return []
      }
      const counts = []
      if (this.source === 'property') {
        counts.push({
          count: this.allPlans.filter(x => x.referenceId.includes('prop_')).length,
          value: 'property'
        })
      }
      counts.push({
        count: this.allPlans.filter(x => x.referenceId.includes('building_')).length,
        value: 'building'
      })
      counts.push(...buildValueCounts<string>(this.allPlans.map(obj => this.findObjectById(obj.referenceId)?.category), ['UNDEFINED_CATEGORY']))
      return counts
    },
    nbrTotalOfHits() {
      return this.listPlans.length
    },
    selectedEntriesIds() {
      return this.selectAllEntries ? this.listPlans.map(x => x.id || x.internalId) : this.selectedEntriesGlobal.map(obj => obj.id || obj.internalId)
    }
  },
  watch: {
    currentPage: function () {
      this.handleCheckboxesInsideTable(false)
      this.$refs.header_checkbox.checked = false
    },
    nbrPerPage: function (newVal) {
      localStorage.setItem('investmentPlanningTab-nbrPerPage', newVal)
    },
    listPlanningCosts: {
      handler: function (newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.getPlanCosts(newVal)
        }
      },
      deep: true
    },
    categoryFilter: {
      handler: function (newVal) {
        if (newVal.length > 0 && this.filters && this.filters.searchCategories) {
          this.searchCategories = this.categoryFilter.filter(x => this.filters.searchCategories.some(y => y === x.value))
          this.filterPlansList()
        }
      }
    }
  },
  created() {
    // Initialize the debounced function in the created lifecycle hook
    this.debouncedFilterPlansList = debounce(() => {
      this.filterPlansList()
    }, 300)
  },
  mounted: function () {
    this.modalHideHandler = (bvEvent, modalId) => {
      if (modalId === 'createPlanningCostModal') {
        if (this.$route.query && this.$route.query.addNewComponent) {
          const query = Object.assign({}, this.$route.query)
          delete query.addNewComponent
          this.$router.replace({ query })
        }
      }
    }
    this.$root.$on('bv::modal::hide', this.modalHideHandler)

    this.getPlanCosts(this.listPlanningCosts)
    this.calculatePlanningCostsByYear()

    this.nbrPerPage = localStorage.getItem('investmentPlanningTab-nbrPerPage') ? localStorage.getItem('investmentPlanningTab-nbrPerPage') === 'all' ? 'all' : parseInt(localStorage.getItem('investmentPlanningTab-nbrPerPage')) : 5
    SearchApi.loadSettings(this.accountId).then(response => {
      const settings = response.tableSettings
      let hasOldList = false
      if (settings.length > 0) {
        settings.forEach(setting => {
          if (setting.columns.length > 0 && setting.table === 'planningCostsProperty') {
            hasOldList = true
            this.loadFields(setting)
          }
        })
      }
      if (hasOldList === false) {
        this.fields.push(...this.dataFields.filter(x => x.selected))
        this.selectedFields = JSON.parse(JSON.stringify(this.dataFields))
      }
    }).catch(e => {
      console.log(e)
    })
    if (this.filters && this.filters.searchText) {
      this.searchText = this.filters.searchText
      this.filterPlansList()
    }
  },
  beforeDestroy() {
    if (this.modalHideHandler) {
      this.$root.$off('bv::modal::hide', this.modalHideHandler)
    }
  },
  methods: {
    filtersChanged() {
      const query = {} as queryParams
      if (this.searchText) {
        query.searchText = this.searchText
      }
      if (this.searchCategories && this.searchCategories.length > 0) {
        query.searchCategories = this.searchCategories.map(x => x.value)
      }
      this.$emit('filters-changed', this.isFiltered, query)
    },
    initFilter() {
      this.searchText = ''
      this.searchCategories = []
    },
    customLabelCategory(item) {
      if (item.value === 'property') {
        return this.$t('message.propertiesView.tabs.property') + ' (' + item.count + ')'
      }
      if (item.value === 'building') {
        return this.$t('message.generic.building') + ' (' + item.count + ')'
      }
      return this.$t('message.onBoarding.buildings.objects.category.' + item.value.toLowerCase()) + ' (' + item.count + ')'
    },
    calculatePlanningCostsByYear(item = null) {
      this.planningCostsByYear.forEach(x => {
        x.value = this.listPlans.reduce((sum, plan) => {
          if (plan.date && plan.date.year === x.year) {
            return sum + (parseFloat(plan.costs) || 0)
          }
          return sum
        }, 0)
      })
      if (item) {
        this.emitChanges(item)
      }
    },
    getPlanCosts() {
      this.isBusy = true
      const list = JSON.parse(JSON.stringify(this.listPlanningCosts))
      this.listPlans = list.map((item: InvestPlanPlannedCosts) => {
        return {
          ...item,
          selected: false
        }
      })
      this.allPlans = this.listPlans
      this.getListColoredItems()
      this.isBusy = false
    },
    loadFields(setting) {
      const result = this.getFields(this.dataFields, setting)
      this.fields = result?.fields
      this.selectedFields = result?.selectedFields
      this.$refs.plansTable?.refresh()
    },
    saveFields(data) {
      const columns = data.filter(x => x.selected === true).map(y => y.key)
      SearchApi.saveSettings(this.accountId, columns, 'planningCostsProperty').then(response => {
        const setting = response.tableSettings
        if (setting && setting.columns.length > 0) {
          this.loadFields(setting)
        }
        this.$bvModal.hide('assembleListModal')
      })
    },
    openAssembleListModal() {
      this.$bvModal.show('assembleListModal')
    },
    hideEditPlansModal() {
      this.$refs.editPlansModal.hide()
      this.showEditModal = false
      this.editAction = ''
    },
    bulkEditPlans(type) {
      this.editAction = type
      this.showEditModal = true
      this.$nextTick(() => {
        this.$refs.editPlansModal.show()
      })
    },
    async applyEditChanges() {
      if (this.editAction === 'plannedDate') {
        this.selectedEntriesIds.forEach(id => {
          this.listPlans.forEach(plan => {
            if (plan.id && id ? plan.id === id : plan.internalId === id) {
              plan.date = this.toObject(this.newPlannedDate)
              plan.dateString = this.newPlannedDate
              plan.modified = true
            }
          })
        })
      } else if (this.editAction === 'plannedCosts') {
        this.selectedEntriesIds.forEach(id => {
          this.listPlans.forEach(plan => {
            if (plan.id && id ? plan.id === id : plan.internalId === id) {
              plan.costs = this.newPlannedCost
              plan.modified = true
            }
          })
        })
      }
      this.$emit('change-plan', this.listPlans)
      this.calculatePlanningCostsByYear()
      if (this.readOnly) {
        this.$emit('save-data')
      } else {
        this.emitChanges()
      }
      this.hideEditPlansModal()
      this.handleCheckboxesInsideTable(false)
      // this.$refs.header_checkbox.checked = false
    },
    showCreatePlanModalClicked() {
      this.showCreatePlanModal = true
      this.$nextTick(() => {
        this.$refs.createPlanningCostModal.show()
      })
    },
    hideCreatePlanModal() {
      this.$refs.createPlanningCostModal.hide()
      this.showCreatePlanModal = false
      this.selectedEntries = []
    },
    createPlanClicked() {
      this.loadingAddPlan = true
      // this.selectedEntries.forEach(entry => {
      //   if (entry.includes('prop')) {
      //     this.addNewPropertyPlan()
      //   } else if (entry.includes('building')) {
      //     this.addNewBuildingPlan(entry)
      //   } else {
      //     this.addNewObjectPlan(entry)
      //   }
      // })
      this.$emit('plan-added', this.selectedEntries)
      this.hideCreatePlanModal()
      this.loadingAddPlan = false
      // this.emitChanges()
    },
    dateToObject(item) {
      const planIndex = this.listPlans.findIndex(plan => plan.id && item.id ? plan.id === item.id : plan.internalId === item.internalId)
      if (planIndex !== -1) {
        this.listPlans[planIndex].date = this.toObject(this.listPlans[planIndex].dateString)
      }
      this.calculatePlanningCostsByYear(item)
    },
    getListColoredItems() {
      this.listColoredItems = this.listPlans.reduce((acc, plan) => {
        const referenceId = plan.referenceId
        if (!acc[referenceId]) {
          acc[referenceId] = []
        }
        acc[referenceId].push(plan.id || plan.internalId)
        return acc
      }, {})
    },
    rowClass(item, type) {
      if (type === 'row') {
        let indexColor = -1
        Object.entries(this.listColoredItems).forEach(([key, value], index) => {
          const indexComp = (value as Array<string>).find((plan) => plan === item.id || plan === item.internalId)
          if (indexComp) {
            indexColor = index
          }
        })
        return indexColor % 2 === 0 ? '' : 'dark_line'
      }
      return ''
    },
    findObjectById(id) {
      return this.objectsList.find(obj => obj.id === id)
    },
    findBuildingById(id) {
      return this.buildingsList.find(building => building.id === id)
    },
    filterPlansList() {
      this.$nextTick(function () {
        this.listPlans = this.allPlans.filter(item => {
          const nameSearch = item.name ? item.name.toLowerCase().includes(this.searchText.toLowerCase()) : null
          const bkpHSearch = item.bkp ? item.bkp.includes(this.searchText.toLowerCase()) : null
          const categoryMapped = this.searchCategories.map(x => x.value)
          let cdtCategory
          if (item.referenceId.includes('prop_')) {
            cdtCategory = categoryMapped.includes('property') || this.searchCategories.length === 0
          } else if (item.referenceId.includes('building_')) {
            cdtCategory = categoryMapped.includes('building') || this.searchCategories.length === 0
          } else {
            cdtCategory = categoryMapped.includes(this.findObjectById(item.referenceId).category) || this.searchCategories.length === 0
          }
          let cdtNameObj
          if (item.referenceId.includes('prop_')) {
            cdtNameObj = this.property.name ? (this.property.name.toLowerCase()).includes(this.searchText.toLowerCase()) : null
          } else if (item.referenceId.includes('building_')) {
            cdtNameObj = this.findBuildingById(item.referenceId).name ? (this.findBuildingById(item.referenceId).name.toLowerCase()).includes(this.searchText.toLowerCase()) : null
          } else {
            cdtNameObj = this.findObjectById(item.referenceId).name ? (this.findObjectById(item.referenceId).name.toLowerCase()).includes(this.searchText.toLowerCase()) : null
          }
          return (bkpHSearch || nameSearch || cdtNameObj) && cdtCategory
        })
      })
      this.filtersChanged()
    },
    searchInputText() {
      this.debouncedFilterPlansList()
      this.filtersChanged()
    },
    fillOutLifeSpan(item) {
      if (item.calculatedReplacement) {
        let date = new Date(item.dateString)
        return item.calculatedReplacement - date.getFullYear()
      }
      return ''
    },
    getYear(date) {
      if (date) {
        let d = new Date(this.$options.filters.objectToDateInput(date))
        return d.getFullYear()
      }
    },
    fillOutCalculatedReplacement(item) {
      if (item.dateString) {
        let date = new Date(item.dateString)
        return date.getFullYear() + (item.lifeSpan ? parseInt(item.lifeSpan) : 0)
      }
      return ''
    },
    getPropertyName() {
      return this.property ? this.property.name : ''
    },
    getBuildingName(id) {
      return (this.buildingsList.length > 0 && this.buildingsList.find(x => x.id === id)) ? this.buildingsList.find(x => x.id === id).name : ''
    },
    getObjectName(id) {
      return (this.objectsList.length > 0 && this.objectsList.find(x => x.id === id)) ? this.objectsList.find(x => x.id === id).name : ''
    },
    itemSelected(item, checked) {
      if (checked) {
        this.selectedEntries.push(item.id)
      } else {
        this.selectedEntries = this.selectedEntries.filter(entry => entry !== item.id)
      }
    },
    itemSelectedGlobal(item, checked) {
      this.plansList.forEach((plan: SelectablePlan) => {
        if (plan.id === item.id) {
          plan.selected = checked
        }
      })
      if (checked) {
        this.selectedEntriesGlobal.push(item)
        if (this.selectedEntries.length === this.nbrPerPage) {
          this.selectAllVisibleEntries = true
        }
      } else {
        this.selectedEntriesGlobal = this.selectedEntriesGlobal.filter(entry => item.internalId && entry.internalId ? entry.internalId !== item.internalId : entry.id && item.id ? entry.id !== item.id : false)
        this.selectAllEntries = false
        this.selectAllVisibleEntries = false
      }
    },
    handleCheckboxesInsideTable(value) {
      if (this.$refs.plansTable) {
        const allCheckboxesPlans = this.$refs.plansTable.$children[1].$children
        this.handleCheckbox(allCheckboxesPlans, value)
        this.selectAllVisibleEntries = value
      }
    },
    emitChanges(plan) {
      if (plan) {
        const index = this.listPlans.findIndex(c => c.id === plan.id)
        if (index !== -1) {
          this.listPlans[index] = plan
          this.listPlans[index].modified = true
        }
      }
      this.$emit('change-plan', this.listPlans)
    },
    hideDeletePlanModal() {
      this.$refs.confirmationModalDeletePlans.hide()
      this.showDeleteModal = false
    },
    deletePlansClicked() {
      this.showDeleteModal = true
      this.$nextTick(() => {
        this.$refs.confirmationModalDeletePlans.show()
      })
    },
    deletePlan() {
      // separate new components and existing components
      const { internalIds, ids } = this.selectedEntriesIds.reduce((acc, id) => {
        if (typeof id === 'string' && id.startsWith('plannedCost_')) {
          acc.ids.push(id)
        } else {
          acc.internalIds.push(id)
        }
        return acc
      }, { internalIds: [], ids: [] })

      if (internalIds.length > 0) {
        this.listPlans = this.listPlans.filter(plan => !internalIds.includes(plan.internalId))
      }

      if (ids.length > 0) {
        this.$emit('delete-plan', ids)
        this.hideDeletePlanModal()
      } else {
        this.hideDeletePlanModal()
      }
      // this.$emit('change-plan', this.listPlans)
      this.handleCheckboxesInsideTable(false)
      this.selectedEntriesGlobal = []
    }
  }
})
</script>

<style lang="scss" scoped>
.suggestion-container-bottom {
  position: absolute !important;
  background-color: white;
  z-index: 1000;
  border: 1px solid #ddd;
  max-height: 200px;
  overflow-y: auto;
  top: 100%;
  left: 0;
  right: 0;
}

.suggestion-container-top {
  position: absolute !important;
  background-color: white;
  z-index: 1000;
  border: 1px solid #ddd;
  max-height: 200px;
  overflow-y: auto;
  bottom: 100%;
  left: 0;
  right: 0;
}
:deep(.dark_line) {
  background-color: #eee;
  td {
    background-color: #eee !important;
  }
}
:deep(.width-8) {
  width: 8%;
}
:deep(.width-15) {
  width: 15%;
}
:deep(.width-20) {
  width: 20%;
}
:deep(.optionalValue) {
  background-color: #FFF9E6;
  td {
    background-color: #FFF9E6 !important;
  }
}
</style>
